import { useEffect, useReducer, useState } from 'react'
import dayjs from 'dayjs'
import { Assignee, PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { ArrowRefresh, Calendar2, CalendarCheck, Clock1, Copy, Edit1, Trash2 } from 'blaze-ui/icons'
import Globe from 'blaze-ui/icons/20/Globe'
import { Export } from 'almanac-ui/icons'

import { ExportModal } from 'components/ExportModal/ExportModal'
import { useModals } from 'almanac-ui/Modal'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { DOMSerializer } from 'prosemirror-model'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

import { UserIntent } from './Types'
import { usePostProperties } from 'components/ArticlePostPreview/usePostProperties'
import { useIsChannelPostable } from './utils'
import { useRootStore } from 'stores/RootStore'
import { SocialChannelType, platformNameFromChannel } from 'common/ShareSheetV2/Screens/Default/Default'
import { usePublishScheduledPost } from 'hooks/queries/scheduledPosts/usePublishScheduledPost'
import { PostNowConfirmationModal } from './PostNowConfirmationModal'
import { IntegrationRequiredModal } from 'components/IntegrationRequiredModal/IntegrationRequiredModal'
import { providerFromChannelType } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useQueryClient } from 'react-query'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { useScheduledPost } from 'hooks/queries/scheduledPosts/useScheduledPost'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useBlogIntegrations } from 'hooks/useBlogIntegrations'

export interface PostStatusDropdownState {
  selectedDate: string
  userIntent: UserIntent | null
  calendarOpen: boolean
  loading: boolean
  status: PostStatusPropertyValue
  channel: ChannelType
  initialized: boolean
}

export type PostStatusDropdownOption = {
  icon: React.ReactNode
  label: string
  subLabel: string
  action: UserIntent
}

const setAReminderToPostOption: PostStatusDropdownOption = {
  icon: <Clock1 size={20} />,
  label: 'Set a reminder to post',
  action: UserIntent.ScheduleReminder,
  subLabel: 'Get a notification on the scheduled time & date',
}
const changeReminderDate: PostStatusDropdownOption = {
  icon: <Edit1 size={20} />,
  label: 'Change Reminder Date',
  action: UserIntent.ScheduleReminder,
  subLabel: 'Get a notification on the scheduled time & date',
}
const changeAutoPostDate: PostStatusDropdownOption = {
  icon: <Calendar2 size={20} />,
  label: 'Change Auto Post Date',
  action: UserIntent.ScheduleAutoPost,
  subLabel: 'Get a notification on the scheduled time & date',
}

const removeReminder: PostStatusDropdownOption = {
  icon: <Trash2 size={20} />,
  label: 'Remove Reminder',
  action: UserIntent.RemoveReminder,
  subLabel: 'Remove the reminder from the post',
}
const cancelAutoPostOption: PostStatusDropdownOption = {
  icon: <Trash2 size={20} />,
  label: 'Cancel Auto Post',
  action: UserIntent.RemoveReminder,
  subLabel: 'Cancel the auto post',
}
const scheduleAutoPostOption: PostStatusDropdownOption = {
  icon: <CalendarCheck size={20} />,
  label: 'Schedule to post automatically',
  action: UserIntent.ScheduleAutoPost,
  subLabel: 'Automatically post your content on a scheduled time & date',
}

const markAsPostedOption: PostStatusDropdownOption = {
  icon: <Globe size={20} />,
  label: 'Mark as posted',
  action: UserIntent.MarkAsPosted,
  subLabel: 'Changes the status to "Posted" on the content calendar',
}

const markAsNotPostedOption: PostStatusDropdownOption = {
  icon: <ArrowRefresh size={20} />,
  label: 'Change status to Draft',
  action: UserIntent.MarkAsNotPosted,
  subLabel: 'Revert the status Draft',
}

const exportOption: PostStatusDropdownOption = {
  icon: <Export size={20} />,
  label: 'Export',
  action: UserIntent.Export,
  subLabel: 'Use Zapier or WordPress to export your content',
}

const copyDocContentOption = {
  icon: <Copy size={20} />,
  label: 'Copy doc content',
  action: UserIntent.CopyDocContent,
  subLabel: 'Copy your doc to the clipboard to paste into another product',
}

export type PostStatusDropdownAction =
  | { type: 'SELECT_DATE'; date: string }
  | { type: 'CLOSE_CALENDAR' }
  | { type: UserIntent }
  | { type: 'INITIALIZE'; selectedDate: string; status: PostStatusPropertyValue; channel: ChannelType }
  | { type: 'SET_STATUS'; status: PostStatusPropertyValue }

const initialState: PostStatusDropdownState = {
  selectedDate: dayjs().hour(9).minute(0).toISOString(),
  userIntent: null,
  calendarOpen: false,
  loading: true,
  status: PostStatusPropertyValue.Draft,
  channel: ChannelType.BlogPost,
  initialized: false,
}

function calendarReducer(state: PostStatusDropdownState, event: PostStatusDropdownAction): PostStatusDropdownState {
  switch (event.type) {
    case UserIntent.ScheduleReminder:
    case UserIntent.ScheduleAutoPost:
      return {
        ...state,
        calendarOpen: true,
        userIntent: event.type,
      }
    case UserIntent.MarkAsPosted:
      return {
        ...state,
        userIntent: event.type,
      }
    case 'SELECT_DATE':
      return {
        ...state,
        selectedDate: event.date,
      }
    case 'CLOSE_CALENDAR':
      return {
        ...state,
        userIntent: null,
        calendarOpen: false,
      }
    case 'SET_STATUS':
      return {
        ...state,
        status: event.status as PostStatusPropertyValue,
      }
    case 'INITIALIZE':
      return {
        ...state,
        selectedDate: event.selectedDate,
        status: event.status,
        channel: event.channel,
        initialized: true,
      }
    default:
      return state
  }
}

const getAvailableOptions = ({
  status,
  hasProseInstance,
  inCalendarView,
  isPostable,
  isMobile = false,
  channel,
  hasBlogIntegration = false,
}: {
  status: PostStatusPropertyValue | undefined
  hasProseInstance: boolean
  inCalendarView: boolean
  isPostable: boolean
  isMobile?: boolean
  channel?: ChannelType
  hasBlogIntegration?: boolean
}): PostStatusDropdownOption[][] => {
  let contentOptions: PostStatusDropdownOption[] = []
  if (!isMobile) contentOptions = hasProseInstance ? [exportOption, copyDocContentOption] : [exportOption]

  // Helper function to filter out date changing options when in calendar view
  const filterDateOptions = (options: PostStatusDropdownOption[]) => {
    return inCalendarView
      ? options.filter((option) => option !== changeReminderDate && option !== changeAutoPostDate)
      : options
  }

  // Helper function to filter out schedule auto post option for blog posts without integration
  const filterScheduleOptions = (options: PostStatusDropdownOption[]) => {
    if (channel === ChannelType.BlogPost && !hasBlogIntegration) {
      return options.filter((option) => option !== scheduleAutoPostOption)
    }
    return options
  }

  if (isPostable) {
    switch (status) {
      case PostStatusPropertyValue.ReminderScheduled:
        return [
          filterDateOptions([changeReminderDate, removeReminder, markAsPostedOption]),
          filterScheduleOptions([scheduleAutoPostOption, ...contentOptions]),
        ]
      case PostStatusPropertyValue.AutoPostScheduled:
        return [
          filterDateOptions([changeAutoPostDate, cancelAutoPostOption, markAsPostedOption]),
          [setAReminderToPostOption, ...contentOptions],
        ]
      case PostStatusPropertyValue.Posted:
        return [
          [markAsNotPostedOption],
          filterScheduleOptions([scheduleAutoPostOption, setAReminderToPostOption, ...contentOptions]),
        ]
      case PostStatusPropertyValue.Draft:
      case PostStatusPropertyValue.Failed:
        return [
          filterScheduleOptions([scheduleAutoPostOption, setAReminderToPostOption, markAsPostedOption]),
          contentOptions,
        ]
      default:
        return []
    }
  } else {
    switch (status) {
      case PostStatusPropertyValue.ReminderScheduled:
        return [filterDateOptions([changeReminderDate, removeReminder, markAsPostedOption]), contentOptions]
      case PostStatusPropertyValue.Posted:
        return [[markAsNotPostedOption], [setAReminderToPostOption, ...contentOptions]]
      case PostStatusPropertyValue.Draft:
      case PostStatusPropertyValue.Failed:
        return [[setAReminderToPostOption, markAsPostedOption], contentOptions]
      default:
        return []
    }
  }
}

export function usePostStatusDropdownState({
  assignee,
  afterExecute,
  inCalendarView = false,
  statusOverride,
  isMobile = false,
}: {
  assignee: Assignee
  afterExecute?: (intent: UserIntent) => void
  inCalendarView?: boolean
  statusOverride?: PostStatusPropertyValue
  isMobile?: boolean
}) {
  const [isLoading, setIsLoading] = useState(false)
  const [state, dispatch] = useReducer(calendarReducer, initialState)
  const { data: integrationsData } = useExternalIntegrations()
  const { connectedBlogIntegration } = useBlogIntegrations()
  const { files } = useRootStore()
  const file = files.get(assignee.id)

  const { data: scheduledPost, isLoading: isScheduledPostLoading } = useScheduledPost(
    {
      id: file?.scheduled_post_id as number,
    },
    {
      enabled: !!file?.scheduled_post_id,
    },
  )
  useEffect(() => {
    files.fetch(assignee.id)
  }, [assignee.id, files])
  const { openModal } = useModals()
  const queryClient = useQueryClient()

  const { updatePostAt, updateStatus, status, postAt } = usePostProperties(assignee)
  const proseInstance = useProseInstance(false)
  const { isChannelPostable } = useIsChannelPostable()
  const publishScheduledPost = usePublishScheduledPost()

  const isPostable = isChannelPostable(file?.channel || undefined)

  const hasIntegration = (() => {
    if (file?.scheduled_post_id && !isScheduledPostLoading && scheduledPost !== undefined) {
      return !!scheduledPost?.external_integration
    }

    if (!file?.channel || !integrationsData?.external_integrations) {
      return false
    }

    const requiredProvider = providerFromChannelType[file.channel as SocialChannelType]
    if (!requiredProvider) {
      return false
    }

    return integrationsData.external_integrations.some((integration) => integration.provider === requiredProvider)
  })()

  const updatePost = async ({
    status: newStatus,
    postAt: newPostAt,
  }: {
    status?: PostStatusPropertyValue
    postAt?: string | null
  }) => {
    const promises = []
    if (newPostAt !== undefined) {
      promises.push(updatePostAt(newPostAt))
    }
    if (newStatus) {
      promises.push(updateStatus(newStatus))
    }
    await Promise.all(promises)
    queryClient.invalidateQueries(calendarKeys.all)
  }

  const handleCopy = () => {
    const { currentView } = proseInstance
    if (!currentView) return

    const doc = currentView.state.doc

    const serializer = currentView.someProp('clipboardSerializer') || DOMSerializer.fromSchema(currentView.state.schema)
    let fragment = serializer.serializeFragment(doc.content)
    let tmp = document.createElement('div')
    tmp.appendChild(fragment)

    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([tmp.innerHTML], { type: 'text/html' }),
      'text/plain': new Blob([tmp.innerHTML], { type: 'text/plain' }),
    })

    navigator.clipboard.write([clipboardItem]).then(() => {
      showToastMessage('Doc contents copied!')
    })
  }

  useEffect(() => {
    if (postAt) {
      dispatch({ type: 'SELECT_DATE', date: postAt })
    }
  }, [postAt])

  const handleIntent = (intent: UserIntent) => {
    if (inCalendarView) {
      executeAction(intent)
      return
    }
    if (intent === UserIntent.ScheduleAutoPost) {
      if (!hasIntegration) {
        openModal(IntegrationRequiredModal, {
          integration: providerFromChannelType[file?.channel as SocialChannelType] as ExternalIntegrationEnum,
        })
      } else {
        dispatch({ type: intent })
      }
    } else if (intent === UserIntent.ScheduleReminder) {
      dispatch({ type: intent })
    } else {
      executeAction(intent)
    }
  }

  const postNow = async () => {
    if (!file || !file.scheduled_post_id) return
    await updatePostAt(dayjs().toISOString())
    await publishScheduledPost.mutateAsync({ id: file.scheduled_post_id })
  }

  const showPostNowConfirmationModal = () => {
    // For blog posts, use the actual connected integration instead of hardcoded channel name
    let provider: string
    let providers: string[] | undefined

    if (file?.channel === ChannelType.BlogPost && connectedBlogIntegration) {
      // Use the actual connected blog integration
      provider = connectedBlogIntegration.provider
      providers = [connectedBlogIntegration.provider]
    } else {
      // For social posts, use the channel-based provider name
      provider = platformNameFromChannel[file?.channel as SocialChannelType]
    }

    openModal(PostNowConfirmationModal, {
      provider,
      providers,
      onConfirm: postNow,
    })
  }

  const executeAction = async (intent: UserIntent, postAt?: string) => {
    switch (intent) {
      case UserIntent.ScheduleReminder:
        await updatePost({ status: PostStatusPropertyValue.ReminderScheduled, postAt: postAt || state.selectedDate })
        showToastMessage('Reminder set', 'success')
        break
      case UserIntent.PostNow:
        showPostNowConfirmationModal()
        break
      case UserIntent.PostNowWithoutConfirmation:
        setIsLoading(true)
        await postNow()
        setIsLoading(false)
        break
      case UserIntent.ScheduleAutoPost:
        setIsLoading(true)
        await updatePost({ status: PostStatusPropertyValue.AutoPostScheduled, postAt: postAt || state.selectedDate })
        setIsLoading(false)
        showToastMessage('Scheduled to post automatically', 'success')
        break
      case UserIntent.MarkAsPosted:
        await updatePost({ status: PostStatusPropertyValue.Posted, postAt: dayjs().toISOString() })
        if (!isMobile) showToastMessage('Marked as posted', 'success')
        break
      case UserIntent.MarkAsNotPosted:
        await updatePost({ status: PostStatusPropertyValue.Draft, postAt: postAt || state.selectedDate })
        if (!isMobile) showToastMessage('Moved back to draft', 'success')
        break
      case UserIntent.RemoveReminder:
        await updatePost({ status: PostStatusPropertyValue.Draft })
        showToastMessage('Post unscheduled successfully', 'success')
        break
      case UserIntent.Unschedule:
        await updatePost({ status: PostStatusPropertyValue.Draft, postAt: null })
        showToastMessage('Post unscheduled successfully', 'success')
        break
      case UserIntent.Export:
        openModal(ExportModal, { articleId: assignee.id })
        break
      case UserIntent.CopyDocContent:
        handleCopy()
        break
    }
    afterExecute?.(intent)
    dispatch({ type: 'CLOSE_CALENDAR' })
  }

  return {
    state,
    dispatch,
    executeAction,
    handleIntent,
    isLoading,
    isPostable,
    hasIntegration,
    options: getAvailableOptions({
      status: statusOverride || status?.value,
      hasProseInstance: !!proseInstance.currentView,
      inCalendarView,
      isPostable,
      isMobile,
      channel: file?.channel as ChannelType | undefined,
      hasBlogIntegration: !!connectedBlogIntegration,
    }).filter((sub) => sub.length),
  }
}
